import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Stack, Chip, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, IconButton, Button,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert,
  Divider,
} from '@mui/material';
import {
  Search, Refresh, Save, Close, Edit, AttachMoney,
  LocalAtm, Inventory2, ChevronRight, CheckCircle,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import {
  consumablesUrl, consumablePricingUrl, consumablePricingUpdateUrl,
} from '../BaseURL';

const PRIMARY = '#41a386';
const PRIMARY_DARK = '#2f8068';
const BORDER  = '#e5e9ef';
const MUTED   = '#64748b';
const STRONG  = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

const formatGHS = (n) =>
  new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(Number(n) || 0);

const ConsumablePricing = () => {
  // ── Catalogue (left pane) ────────────────────────────────────────────────
  const [items, setItems]                 = useState([]);
  const [itemsLoading, setItemsLoading]   = useState(false);
  const [itemSearch, setItemSearch]       = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories]       = useState([]);
  const [selectedId, setSelectedId]       = useState(null);

  // ── Pricing matrix (right pane) ──────────────────────────────────────────
  const [pricingRows, setPricingRows]     = useState([]);
  const [consumable, setConsumable]       = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [editMode, setEditMode]           = useState(false);
  const [edits, setEdits]                 = useState({});   // insurance_id -> { topPrice, cost, code, unit }
  const [saving, setSaving]               = useState(false);

  // ── Bulk apply helpers ───────────────────────────────────────────────────
  const [bulkTopPrice, setBulkTopPrice] = useState('');
  const [bulkCost,     setBulkCost]     = useState('');

  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) {
        const u = JSON.parse(raw);
        return u?.username || u?.name || '';
      }
    } catch (_) {}
    return '';
  }, []);

  // ── Loaders ──────────────────────────────────────────────────────────────
  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const { data } = await axios.get(consumablesUrl, { params: { status: 'active' } });
      if (data?.success) {
        setItems(data.results || []);
        setCategories(data.categories || []);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('[consumable pricing] items load failed:', err.message);
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  const loadPricing = useCallback(async (id) => {
    if (!id) {
      setPricingRows([]); setConsumable(null); return;
    }
    setPricingLoading(true);
    try {
      const { data } = await axios.get(consumablePricingUrl, { params: { consumableId: id } });
      if (data?.success) {
        setPricingRows(data.results || []);
        setConsumable(data.consumable || null);
        // Seed edits from server values so toggling Edit doesn't blank them.
        const seed = {};
        (data.results || []).forEach(r => {
          seed[r.insurance_id] = {
            topPrice: r.top_price,
            cost:     r.cost,
            code:     r.code || '',
            unit:     r.unit || (data.consumable?.base_unit || ''),
          };
        });
        setEdits(seed);
      } else {
        setPricingRows([]);
        setConsumable(null);
      }
    } catch (err) {
      console.error('[consumable pricing] load failed:', err.message);
      setPricingRows([]);
      setConsumable(null);
    } finally {
      setPricingLoading(false);
    }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadPricing(selectedId); setEditMode(false); }, [selectedId, loadPricing]);

  // Filtered catalogue
  const filteredItems = useMemo(() => {
    return items.filter(i => {
      const matchSearch = !itemSearch
        || i.name?.toLowerCase().includes(itemSearch.toLowerCase())
        || i.category?.toLowerCase().includes(itemSearch.toLowerCase());
      const matchCat = categoryFilter === 'all' || i.category === categoryFilter;
      return matchSearch && matchCat;
    });
  }, [items, itemSearch, categoryFilter]);

  const pricedCount = pricingRows.filter(r => r.priced).length;

  // ── Edit handlers ────────────────────────────────────────────────────────
  const setField = (insuranceId, key, value) => {
    setEdits(prev => ({
      ...prev,
      [insuranceId]: { ...(prev[insuranceId] || {}), [key]: value },
    }));
  };

  const cancelEdit = () => {
    // Reset edits from server snapshot.
    const seed = {};
    pricingRows.forEach(r => {
      seed[r.insurance_id] = {
        topPrice: r.top_price,
        cost:     r.cost,
        code:     r.code || '',
        unit:     r.unit || (consumable?.base_unit || ''),
      };
    });
    setEdits(seed);
    setEditMode(false);
    setBulkTopPrice('');
    setBulkCost('');
  };

  const applyBulk = () => {
    if (!editMode) return;
    setEdits(prev => {
      const next = { ...prev };
      pricingRows.forEach(r => {
        const cur = { ...(next[r.insurance_id] || {}) };
        if (bulkTopPrice !== '') cur.topPrice = parseFloat(bulkTopPrice) || 0;
        if (bulkCost     !== '') cur.cost     = parseFloat(bulkCost)     || 0;
        next[r.insurance_id] = cur;
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedId) return;
    const payloadRows = pricingRows.map(r => {
      const e = edits[r.insurance_id] || {};
      return {
        insuranceId:   r.insurance_id,
        insuranceName: r.insurance_name,
        topPrice:      parseFloat(e.topPrice) || 0,
        cost:          parseFloat(e.cost)     || 0,
        code:          e.code || null,
        unit:          e.unit || consumable?.base_unit || null,
      };
    });

    setSaving(true);
    try {
      const { data } = await axios.post(consumablePricingUpdateUrl, {
        consumableId: selectedId,
        rows:         payloadRows,
        updatedBy:    currentUser || null,
      });
      if (data?.success) {
        Swal.fire({ icon: 'success', title: 'Saved', text: data.message, timer: 1600, showConfirmButton: false });
        setEditMode(false);
        setBulkTopPrice('');
        setBulkCost('');
        loadPricing(selectedId);
      } else {
        Swal.fire({ icon: 'error', title: 'Save failed', text: data?.message });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
          <LocalAtm sx={{ color: PRIMARY, fontSize: 18 }} />
          <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
            Procurement · Consumables
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: STRONG, lineHeight: 1.15 }}>
          Consumable Pricing
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: MUTED, mt: 0.5 }}>
          Set top-up and tariff prices for the insurances each consumable is registered under. To add or remove an insurance from the matrix, edit the consumable from the Register page.
        </Typography>
      </Box>

      {/* Flexbox split — catalog is a fixed-width column, pricing panel
          flex-grows into ALL remaining horizontal space (no dead area on
          the right). Stacks vertically below the md breakpoint. */}
      <Box sx={{
        display: 'flex',
        gap: 1.5,
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch',
      }}>
        {/* LEFT — catalogue (fixed width on md+) */}
        <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
          <Paper elevation={0} sx={{
            border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden',
            bgcolor: '#fff', height: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column',
          }}>
            <Box sx={{ p: 1.25, borderBottom: `1px solid ${BORDER}` }}>
              <TextField
                fullWidth size="small" placeholder="Search consumables…"
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: MUTED }} /></InputAdornment>,
                }}
              />
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>Category</InputLabel>
                <Select label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <MenuItem value="all">All categories</MenuItem>
                  {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {itemsLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress size={20} sx={{ color: PRIMARY }} />
                </Box>
              ) : filteredItems.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center', color: MUTED }}>
                  <Inventory2 sx={{ fontSize: 32, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontSize: '0.82rem' }}>No consumables match.</Typography>
                </Box>
              ) : (
                filteredItems.map(c => {
                  const active = selectedId === c.id;
                  return (
                    <Box key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      sx={{
                        px: 1.5, py: 1, cursor: 'pointer',
                        borderBottom: `1px solid #f1f5f9`,
                        bgcolor: active ? 'rgba(65,163,134,0.10)' : 'transparent',
                        borderLeft: active ? `3px solid ${PRIMARY}` : '3px solid transparent',
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        '&:hover': { bgcolor: active ? 'rgba(65,163,134,0.14)' : '#f8fafc' },
                      }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG }} noWrap>
                          {c.name}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                          {c.category && (
                            <Typography sx={{ fontSize: '0.68rem', color: MUTED }}>{c.category}</Typography>
                          )}
                          <Typography sx={{ fontSize: '0.68rem', color: MUTED }}>
                            {c.base_unit}{c.pack_size > 1 ? ` · ${c.pack_size}/${(c.pack_label || 'pack').toLowerCase()}` : ''}
                          </Typography>
                        </Stack>
                      </Box>
                      {active && <ChevronRight sx={{ color: PRIMARY, fontSize: 18 }} />}
                    </Box>
                  );
                })
              )}
            </Box>
          </Paper>
        </Box>

        {/* RIGHT — pricing matrix (flex:1 takes ALL remaining width) */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper elevation={0} sx={{
            border: `1px solid ${BORDER}`, borderRadius: 2,
            bgcolor: '#fff', minHeight: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column',
            width: '100%',
          }}>
            {!selectedId ? (
              <Box sx={{ p: 8, textAlign: 'center', color: MUTED }}>
                <LocalAtm sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontWeight: 700, color: STRONG, fontSize: '0.95rem' }}>
                  Pick a consumable on the left
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                  Its insurance pricing matrix will appear here.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Header strip */}
                <Box sx={{
                  px: 2, py: 1.5,
                  borderBottom: `1px solid ${BORDER}`,
                  display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
                      Pricing for
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }}>
                      {consumable?.name || `Consumable #${selectedId}`}
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      {consumable?.category && (
                        <Chip size="small" label={consumable.category}
                          sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#f1f5f9', color: STRONG }} />
                      )}
                      <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                        Base unit: <strong style={{ color: STRONG }}>{consumable?.base_unit}</strong>
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                        Priced for <strong style={{ color: STRONG }}>{pricedCount}</strong> / {pricingRows.length} insurers
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Reload">
                      <span>
                        <IconButton size="small" onClick={() => loadPricing(selectedId)} disabled={pricingLoading}
                          sx={{ border: `1px solid ${BORDER}`, color: MUTED }}>
                          {pricingLoading ? <CircularProgress size={14} sx={{ color: PRIMARY }} /> : <Refresh fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {!editMode ? (
                      <Button size="small" variant="contained" disableElevation
                        startIcon={<Edit sx={{ fontSize: 16 }} />}
                        onClick={() => setEditMode(true)}
                        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                        Edit pricing
                      </Button>
                    ) : (
                      <>
                        <Button size="small" onClick={cancelEdit} disabled={saving}
                          sx={{ textTransform: 'none', color: MUTED }}>
                          Cancel
                        </Button>
                        <Button size="small" variant="contained" disableElevation
                          startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Save sx={{ fontSize: 16 }} />}
                          onClick={handleSave} disabled={saving}
                          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                          {saving ? 'Saving…' : 'Save changes'}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Box>

                {/* Bulk apply (edit mode only) */}
                {editMode && (
                  <Box sx={{
                    px: 2, py: 1, bgcolor: 'rgba(65,163,134,0.04)',
                    borderBottom: `1px solid ${BORDER}`,
                    display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
                  }}>
                    <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Bulk apply</Typography>
                    <TextField size="small" type="number" placeholder="Top price"
                      value={bulkTopPrice} onChange={(e) => setBulkTopPrice(e.target.value)}
                      sx={{ width: 130 }}
                      InputProps={{ startAdornment: <InputAdornment position="start">₵</InputAdornment> }} />
                    <TextField size="small" type="number" placeholder="Tariff"
                      value={bulkCost} onChange={(e) => setBulkCost(e.target.value)}
                      sx={{ width: 130 }}
                      InputProps={{ startAdornment: <InputAdornment position="start">₵</InputAdornment> }} />
                    <Button size="small" variant="outlined" onClick={applyBulk}
                      sx={{
                        textTransform: 'none', fontWeight: 700, borderColor: PRIMARY, color: PRIMARY,
                        '&:hover': { borderColor: PRIMARY_DARK, bgcolor: 'rgba(65,163,134,0.06)' },
                      }}>
                      Apply to all rows
                    </Button>
                    <Typography sx={{ fontSize: '0.74rem', color: MUTED, flex: 1, textAlign: 'right' }}>
                      Leave a field blank to keep existing values.
                    </Typography>
                  </Box>
                )}

                {/* Pricing table */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {pricingLoading ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                      <CircularProgress size={24} sx={{ color: PRIMARY }} />
                    </Box>
                  ) : pricingRows.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center', color: MUTED }}>
                      <Typography sx={{ fontWeight: 700, color: STRONG, fontSize: '0.92rem' }}>
                        No active insurance partners
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', mt: 0.5 }}>
                        Add insurances in Admin → Insurance Companies first.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Insurance</TableCell>
                            <TableCell align="right" sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Top price</TableCell>
                            <TableCell align="right" sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Tariff</TableCell>
                            <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Code</TableCell>
                            <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Unit</TableCell>
                            <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pricingRows.map(r => {
                            const e = edits[r.insurance_id] || {};
                            return (
                              <TableRow key={r.insurance_id} hover>
                                <TableCell sx={{ py: 1.25 }}>
                                  <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                                    {r.insurance_name}
                                  </Typography>
                                </TableCell>

                                {/* Top price */}
                                <TableCell align="right" sx={{ py: 1.25 }}>
                                  {editMode ? (
                                    <TextField size="small" type="number"
                                      value={e.topPrice ?? ''}
                                      onChange={(ev) => setField(r.insurance_id, 'topPrice', ev.target.value)}
                                      sx={{ width: 130 }} inputProps={{ min: 0, step: 0.01 }}
                                      InputProps={{ startAdornment: <InputAdornment position="start">₵</InputAdornment> }} />
                                  ) : (
                                    r.priced
                                      ? <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: PRIMARY }}>{formatGHS(r.top_price)}</Typography>
                                      : <Chip size="small" label="Not set" sx={{ height: 20, fontSize: '0.66rem', bgcolor: '#f1f5f9', color: '#94a3b8' }} />
                                  )}
                                </TableCell>

                                {/* Tariff */}
                                <TableCell align="right" sx={{ py: 1.25 }}>
                                  {editMode ? (
                                    <TextField size="small" type="number"
                                      value={e.cost ?? ''}
                                      onChange={(ev) => setField(r.insurance_id, 'cost', ev.target.value)}
                                      sx={{ width: 130 }} inputProps={{ min: 0, step: 0.01 }}
                                      InputProps={{ startAdornment: <InputAdornment position="start">₵</InputAdornment> }} />
                                  ) : (
                                    r.priced
                                      ? <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>{formatGHS(r.cost)}</Typography>
                                      : <Chip size="small" label="Not set" sx={{ height: 20, fontSize: '0.66rem', bgcolor: '#f1f5f9', color: '#94a3b8' }} />
                                  )}
                                </TableCell>

                                {/* Code */}
                                <TableCell sx={{ py: 1.25 }}>
                                  {editMode ? (
                                    <TextField size="small"
                                      value={e.code ?? ''}
                                      onChange={(ev) => setField(r.insurance_id, 'code', ev.target.value)}
                                      sx={{ width: 130 }} placeholder="—" />
                                  ) : (
                                    <Typography sx={{ fontSize: '0.82rem', color: r.code ? STRONG : '#cbd5e1' }}>
                                      {r.code || '—'}
                                    </Typography>
                                  )}
                                </TableCell>

                                {/* Unit */}
                                <TableCell sx={{ py: 1.25 }}>
                                  {editMode ? (
                                    <TextField size="small"
                                      value={e.unit ?? (consumable?.base_unit || '')}
                                      onChange={(ev) => setField(r.insurance_id, 'unit', ev.target.value)}
                                      sx={{ width: 110 }} placeholder={consumable?.base_unit || '—'} />
                                  ) : (
                                    <Typography sx={{ fontSize: '0.82rem', color: STRONG }}>
                                      {r.unit || consumable?.base_unit || '—'}
                                    </Typography>
                                  )}
                                </TableCell>

                                {/* Status */}
                                <TableCell sx={{ py: 1.25 }}>
                                  {r.priced ? (
                                    <Chip size="small" icon={<CheckCircle sx={{ fontSize: 12 }} />} label="Priced"
                                      sx={{
                                        height: 22, fontSize: '0.7rem', fontWeight: 700,
                                        bgcolor: 'rgba(16,185,129,0.10)', color: '#15803D',
                                        border: '1px solid rgba(16,185,129,0.30)',
                                        '& .MuiChip-icon': { color: 'inherit' },
                                      }} />
                                  ) : (
                                    <Chip size="small" label="Pending"
                                      sx={{
                                        height: 22, fontSize: '0.7rem', fontWeight: 700,
                                        bgcolor: 'rgba(234,179,8,0.10)', color: '#a16207',
                                        border: '1px solid rgba(234,179,8,0.30)',
                                      }} />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ConsumablePricing;
